import { useApolloClient, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { RecordFolder } from 'src/graphql';
import { OPTIMISTIC_ID_PREFIX } from 'src/graphql/entities/custom-view';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { MAX_RECORD_FOLDERS_LIMIT } from 'src/graphql/entities/record-folder/constants';
import {
  CREATE_RECORD_FOLDERS_QUERY,
  GET_RECORD_FOLDERS_QUERY,
} from 'src/graphql/entities/record-folder/record-folder.operations';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { GetRecordFolder } from './use-record-folders';

export interface CreateRecordFolder {
  createRecordFolder: RecordFolder[];
}

export const useCreateRecordFolder = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const client = useApolloClient();
  const { user } = useCurrentUserProfile();
  const { showErrorAlert } = useNotifications();

  const [createRecordFolder, { loading: isLoading }] = useMutation<CreateRecordFolder>(
    CREATE_RECORD_FOLDERS_QUERY,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong creating the folder. Please try again.');
      },
      update(cache, { data }) {
        if (!data?.createRecordFolder?.length) return;

        cache.updateQuery<GetRecordFolder>(
          {
            query: GET_RECORD_FOLDERS_QUERY,
            variables: {
              limit: MAX_RECORD_FOLDERS_LIMIT,
              partnerId: selectedPartnerId ?? PLATFORM_PARTNER_ID,
              area: data.createRecordFolder[0].area,
            },
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getEnabledRecordFolders: {
                ...existingData.getEnabledRecordFolders,
                records: [
                  ...existingData.getEnabledRecordFolders.records.filter(
                    (i) =>
                      i.recordFolderId !== data.createRecordFolder[0].recordFolderId &&
                      !i.recordFolderId.startsWith(OPTIMISTIC_ID_PREFIX),
                  ),
                  data.createRecordFolder[0],
                ],
              },
            };
          },
        );
      },
    },
  );

  const createWithOptimisticResponse = useCallback(
    async (data: Pick<RecordFolder, 'name' | 'icon' | 'partnerId' | 'isPrivate' | 'area'>) => {
      const createObject = {
        ...data,
        userId: user?.userId!,
      };
      try {
        const response = await createRecordFolder({
          variables: { data: createObject },
          optimisticResponse() {
            const nowString = new Date().toISOString();
            const tempId = `${OPTIMISTIC_ID_PREFIX}${nowString}`;

            return {
              createRecordFolder: [
                {
                  __typename: 'RecordFolder',
                  ...createObject,
                  id: tempId,
                  recordFolderId: tempId,
                  createdAt: nowString,
                  updatedAt: nowString,
                },
              ],
            };
          },
        });

        const createdFolder = response.data?.createRecordFolder?.[0];

        if (createdFolder?.partnerId === PLATFORM_PARTNER_ID) {
          client.cache.evict({
            fieldName: 'getEnabledRecordFolders',
          });
          client.cache.gc();
        }

        return createdFolder;
      } catch {
        return undefined;
      }
    },
    [client, createRecordFolder, user],
  );

  return {
    createRecordFolder: createWithOptimisticResponse,
    isLoading,
  };
};
